/**
 * EpssChip — CISO-facing EPSS presentation component.
 *
 * Ships under Vulns V4 Slice 3. Swapped into `VulnContextStrip` behind the
 * `VITE_VULNS_V4_EPSS_CHIP_ENABLED` flag once the FIRST.org refresh adapter
 * has populated `epss_percentile` + `epss_refreshed_at` in prod.
 *
 * Renders three bits of info for a CISO glancing at a vuln:
 *   - `EPSS`  label
 *   - the score (2 decimals, e.g. `0.42`)
 *   - a "Top X%" ribbon derived from percentile (e.g. `Top 15%` for 0.85)
 *
 * Optional `epssRefreshedAt` prop enables tiered staleness indicators:
 *   - fresh     (<24h):    no indicator
 *   - slightly-stale (24–48h): muted dot next to the "EPSS" label
 *   - stale     (48h–7d):  amber "Nd old" ribbon
 *   - very-stale (>7d):    red "Stale" badge + em-dash (hides score)
 *
 * Input shape (matches the real `Vulnerability` type — both nullable strings):
 *   `epssScore?: string | null`
 *   `epssPercentile?: string | null`
 *   `epssRefreshedAt?: string | null`
 *
 * Parsing rules:
 *   - Non-numeric / `NaN` / out-of-range (`<0` or `>1`) values are coerced to
 *     null.
 *   - If both are null after parsing, the chip renders a null-state em-dash.
 *   - If only percentile is null/invalid, score renders alone — no "Top X%".
 *   - `Top X%` is `ceil((1 - percentile) * 100)` with a floor of 1%, so a
 *     percentile of 0.999 shows `Top 1%`, and 0.85 shows `Top 15%`.
 */

export interface EpssChipProps {
  epssScore: string | null | undefined
  epssPercentile: string | null | undefined
  epssRefreshedAt?: string | null | undefined
}

function parseFraction(raw: string | null | undefined): number | null {
  if (typeof raw !== 'string') return null
  const n = Number(raw)
  if (!Number.isFinite(n)) return null
  if (n < 0 || n > 1) return null
  return n
}

function topPercent(percentile: number): number {
  // Round to 4 decimals first to eat IEEE-754 drift (e.g. 1 - 0.85 = 0.15000…002).
  const inverse = Math.round((1 - percentile) * 10000) / 10000
  const pct = Math.ceil(inverse * 100)
  return Math.max(pct, 1)
}

type StalenessTier = 'fresh' | 'slightly-stale' | 'stale' | 'very-stale'
const HOUR_MS = 60 * 60 * 1000

function stalenessTier(
  epssRefreshedAt: string | null | undefined
): { tier: StalenessTier; ageDays: number } | null {
  if (!epssRefreshedAt) return null
  const t = new Date(epssRefreshedAt).getTime()
  if (!Number.isFinite(t)) return null
  const ageHours = (Date.now() - t) / HOUR_MS
  if (ageHours < 0) return null
  const ageDays = Math.max(1, Math.floor(ageHours / 24))
  if (ageHours <= 24) return { tier: 'fresh', ageDays }
  if (ageHours <= 48) return { tier: 'slightly-stale', ageDays }
  if (ageHours <= 168) return { tier: 'stale', ageDays }
  return { tier: 'very-stale', ageDays }
}

export function EpssChip({ epssScore, epssPercentile, epssRefreshedAt }: EpssChipProps) {
  const score = parseFraction(epssScore)
  const percentile = parseFraction(epssPercentile)
  const staleness = stalenessTier(epssRefreshedAt)

  // Very-stale state — score is too old to be meaningful; show Stale badge + em-dash.
  if (staleness?.tier === 'very-stale') {
    return (
      <div
        className="flex flex-col"
        aria-label="EPSS score too stale to display"
      >
        <span className="text-xs uppercase text-muted-foreground">EPSS</span>
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="font-medium">—</span>
          <span
            data-epss-staleness="very-stale"
            className="rounded-md border border-red-300 bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-700"
          >
            Stale
          </span>
        </div>
      </div>
    )
  }

  // Null state — render label + em-dash.
  if (score === null && percentile === null) {
    return (
      <div
        className="flex flex-col"
        aria-label="EPSS score unavailable"
      >
        <span className="text-xs uppercase text-muted-foreground">EPSS</span>
        <span className="font-medium">—</span>
      </div>
    )
  }

  const scoreLabel = score !== null ? score.toFixed(2) : '—'
  const top = percentile !== null ? topPercent(percentile) : null

  const ariaLabel =
    top !== null && score !== null
      ? `EPSS ${scoreLabel}, Top ${top}% of vulnerabilities by exploit probability`
      : score !== null
      ? `EPSS ${scoreLabel}`
      : 'EPSS score unavailable'

  return (
    <div className="flex flex-col" aria-label={ariaLabel}>
      <span className="text-xs uppercase text-muted-foreground">
        EPSS
        {staleness?.tier === 'slightly-stale' && (
          <span
            data-epss-staleness="slightly-stale"
            aria-label={`updated ${staleness.ageDays} day${staleness.ageDays > 1 ? 's' : ''} ago`}
            className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/60 align-middle"
          />
        )}
      </span>
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-medium">{scoreLabel}</span>
        {top !== null && (
          <span
            className="rounded-md border px-1.5 py-0.5 text-xs font-medium text-muted-foreground"
            title={`Percentile ${(percentile! * 100).toFixed(1)}`}
          >
            Top {top}%
          </span>
        )}
        {staleness?.tier === 'stale' && (
          <span
            data-epss-staleness="stale"
            className="rounded-md border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-800"
          >
            {staleness.ageDays}d old
          </span>
        )}
      </div>
    </div>
  )
}
